(() => {
  const STORAGE_KEY = 'bb_attribution_v1';
  const ATTRIBUTION_TTL = 90 * 24 * 60 * 60 * 1000;
  const CAMPAIGN_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'];
  const MAX_VALUE_LENGTH = 500;

  const clean = value => String(value || '').trim().slice(0, MAX_VALUE_LENGTH);
  const safeStorage = {
    get() {
      try {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        if (!stored || Date.now() - Number(stored.updated_at || 0) > ATTRIBUTION_TTL) return null;
        return stored;
      } catch { return null; }
    },
    set(value) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch {}
    }
  };

  function safeReferrer() {
    if (!document.referrer) return '';
    try {
      const url = new URL(document.referrer);
      return clean(url.origin === location.origin ? url.pathname : url.origin);
    } catch { return ''; }
  }

  function currentTouch() {
    const params = new URLSearchParams(location.search);
    const touch = {
      landing_path: clean(location.pathname),
      referrer: safeReferrer()
    };
    CAMPAIGN_KEYS.forEach(key => {
      const value = params.get(key);
      if (value) touch[key] = clean(value);
    });
    return touch;
  }

  function captureAttribution() {
    const existing = safeStorage.get();
    const touch = currentTouch();
    const hasCampaign = CAMPAIGN_KEYS.some(key => touch[key]);
    const attribution = existing || { first_touch: touch, last_touch: touch };
    if (hasCampaign || !existing) attribution.last_touch = touch;
    attribution.updated_at = Date.now();
    safeStorage.set(attribution);
    return attribution;
  }

  const attribution = captureAttribution();

  function pageContext() {
    return {
      page_type: clean(document.body?.dataset.pageType),
      page_path: clean(location.pathname),
      product_handle: clean(document.querySelector('[data-product-root]')?.dataset.productHandle)
    };
  }

  function publish(name, data = {}) {
    const payload = { ...pageContext(), ...data };
    document.dispatchEvent(new CustomEvent('bezelbond:analytics', { detail: { name, data: payload } }));
    try {
      const result = window.Shopify?.analytics?.publish?.(name, payload);
      if (result?.catch) result.catch(() => {});
    } catch {}
  }

  function requestType(form) {
    return clean(form.querySelector('[name="contact[Request type]"]')?.value || 'Contact');
  }

  function leadKind(type) {
    const normalized = type.toLowerCase();
    if (normalized.includes('product inquiry')) return 'product_inquiry';
    if (normalized.includes('engagement ring')) return 'ring_consultation';
    if (normalized.includes('custom ring')) return 'custom_ring_design';
    if (normalized.includes('sell')) return 'sell_request';
    if (normalized.includes('source')) return 'sourcing_request';
    return 'contact';
  }

  function appendHidden(form, label, value) {
    if (!value || form.querySelector(`[data-bb-attribution="${label}"]`)) return;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `contact[Attribution - ${label}]`;
    input.value = clean(value);
    input.dataset.bbAttribution = label;
    form.append(input);
  }

  function addAttribution(form) {
    const first = attribution?.first_touch || {};
    const last = attribution?.last_touch || {};
    appendHidden(form, 'First landing page', first.landing_path);
    appendHidden(form, 'First referrer', first.referrer);
    appendHidden(form, 'Last landing page', last.landing_path);
    appendHidden(form, 'Last referrer', last.referrer);
    CAMPAIGN_KEYS.forEach(key => appendHidden(form, key.replaceAll('_', ' ').toUpperCase(), last[key]));
  }

  function instrumentForm(form) {
    if (form.dataset.bbAnalyticsReady) return;
    form.dataset.bbAnalyticsReady = 'true';
    addAttribution(form);
    const kind = leadKind(requestType(form));
    let started = false;
    const start = event => {
      if (started || event.target.matches('[type="hidden"],button')) return;
      started = true;
      publish('bezel_bond:lead_form_started', { lead_kind: kind });
    };
    form.addEventListener('focusin', start);
    form.addEventListener('change', start);
    form.addEventListener('submit', () => {
      addAttribution(form);
      publish('bezel_bond:lead_form_submitted', { lead_kind: kind });
    });
    if (form.querySelector('.form-success')) {
      const key = `bb_success:${location.pathname}:${kind}`;
      try {
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, '1');
      } catch {}
      publish('bezel_bond:lead_submitted', { lead_kind: kind });
    }
  }

  function instrument(scope = document) {
    scope.querySelectorAll('form[action*="/contact"]').forEach(instrumentForm);
  }

  let ringBuilderStarted = false;
  let designerStarted = false;
  document.addEventListener('click', event => {
    const inquiry = event.target.closest('[data-inquiry] > summary');
    if (inquiry) publish('bezel_bond:product_inquiry_opened');

    const appointment = event.target.closest('a');
    if (appointment && /appointment/i.test(appointment.textContent)) {
      publish('bezel_bond:appointment_selected', { destination_path: clean(appointment.pathname) });
    }

    const ringAnswer = event.target.closest('[data-ring-builder] [data-answer]');
    if (ringAnswer) {
      if (!ringBuilderStarted) {
        ringBuilderStarted = true;
        publish('bezel_bond:ring_builder_started');
      }
      publish('bezel_bond:ring_builder_step_completed', { step_name: clean(ringAnswer.dataset.answerKey) });
    }

    if (event.target.closest('[data-ring-download]')) publish('bezel_bond:ring_build_pdf_downloaded', { builder: 'consultation' });
    if (event.target.closest('[data-download-build]')) publish('bezel_bond:ring_build_pdf_downloaded', { builder: 'atelier' });
    if (event.target.closest('[data-product-expand]')) publish('bezel_bond:product_image_expanded');
    if (event.target.closest('[data-product-save]')) publish('bezel_bond:product_saved');
  });

  document.addEventListener('change', event => {
    if (!event.target.closest('[data-designer] [data-config]')) return;
    if (!designerStarted) {
      designerStarted = true;
      publish('bezel_bond:ring_designer_started');
    }
    publish('bezel_bond:ring_designer_option_changed', { option_name: clean(event.target.dataset.config) });
  });

  instrument();
  document.addEventListener('shopify:section:load', event => instrument(event.target));
})();
